import { useEffect, useMemo, useState } from "react"
import LiteratureReviewLayout from "../../Components/LiteratureReviewLayout/LiteratureReviewLayout"
import { DragIndicator, MenuBook, Lock } from "@mui/icons-material"
import { getExtractionData, getExtractionStatus } from "../../Utils/functions"
import Add from "@mui/icons-material/Add"
import AddNewColumnPanel from "../../Components/AddNewColumnPanel/AddNewColumnPanel"
import {
    createExtractionNewColumn,
    reextractColumn,
    deleteExtractionColumn,
} from "../../Utils/functions"
import { DoiButton } from "../../Components/DoiButton/DoiButton"
import CircularProgress from "@mui/material/CircularProgress"
import "./ExtractionDefinition.css"
import { useLiteratureReview } from "../../Context/LiteratureReviewContext"
import { usePoll } from "../../hooks/usePoll"

const ExtractionDefiniton = () => {

    const { id, fromStep, workflowData, setWorkflowData, navigateToStep, setFromStep, getStepsForPage } = useLiteratureReview()
    const { startPolling, stopPolling } = usePoll(2000)
    const [isScreeningSectionHidden, setIsScreeningSectionHidden] = useState(false)
    const [sidebarView, setSidebarView] = useState("default")
    const [isLoading, setIsLoading] = useState(false)
    const [isExtracting, setIsExtracting] = useState(false)
    const [error, setError] = useState("")
    const [extractionArticleValues, setExtractionArticleValues] = useState({})
    const [extractionColumns, setExtractionColumns] = useState([])
    const [columnsList, setColumnsList] = useState([])
    const [editingColumn, setEditingColumn] = useState(null)
    const [expandedFieldKeys, setExpandedFieldKeys] = useState(() => new Set())
    const [question] = useState(workflowData.question ?? "")
    const toggleFieldExpanded = (key) => {
        setExpandedFieldKeys((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const FIELD_VALUE_CLAMP_THRESHOLD = 180

    const screenedArticles = workflowData.screenedArticles ?? []
    const articleUniqueIds = useMemo(
        () => Array.isArray(workflowData.articleUniqueIds) ? workflowData.articleUniqueIds : [],
        [workflowData.articleUniqueIds]
    )

    // when coming from the recommened-abstracts step, full-text-screening and recommended-abstracts steps will no longer appear in the workflow navigation on this step
    const steps = useMemo(() => getStepsForPage("data-extraction-definition"), [getStepsForPage])

    const formatColumnLabel = (key) =>
        (key || "")
            .split(/[-_]/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ")

    const columnDefs = useMemo(
        () =>
            columnsList
                .map((c) => {
                    const colId = c?.id ?? ""
                    const label = c?.label ?? formatColumnLabel(colId)
                    return { id: colId, label }
                })
                .filter((col) => col.id),
        [columnsList]
    )

    const articleIdsSignature = useMemo(() => articleUniqueIds.join(","), [articleUniqueIds])
    const columnIdsSignature = useMemo(() => columnDefs.map((c) => c.id).join(","), [columnDefs])

    const hash = (typeof window !== "undefined" && window.location.hash) || ""
    const isExtractionDefinitionStep = hash.replace(/^#/, "").trim() === "data-extraction-definition"

    useEffect(() => {
        if (!id || !articleIdsSignature || !isExtractionDefinitionStep) return

        const applyExtraction = (data) => {
            if (Array.isArray(data?.extractionColumns)) {
                setExtractionColumns(data.extractionColumns)
                setColumnsList(data.extractionColumns.map((c) => ({ ...c })))
            }
            if (data?.extractionArticleValues != null && typeof data.extractionArticleValues === "object") {
                setExtractionArticleValues(data.extractionArticleValues)
            }
        }

        const clearPoll = stopPolling

        const startPollingFn = () => {
            startPolling(async (stop) => {
                try {
                    const status = await getExtractionStatus(id)
                    applyExtraction(status)
                    if (Array.isArray(status?.extractionColumns) && status.extractionColumns.length > 0) {
                        setIsLoading(false)
                    }
                    if (status?.status === "completed") {
                        setIsLoading(false)
                        setIsExtracting(false)
                        stop()
                    } else if (status?.status === "failed") {
                        setError(status.error || "Failed to load extraction data")
                        setIsLoading(false)
                        setIsExtracting(false)
                        stop()
                    }
                } catch (err) {
                    setError(err?.message || "Failed to load extraction data")
                    setIsLoading(false)
                    setIsExtracting(false)
                    stop()
                }
            })
        }

        const fetchExtraction = async () => {
            setIsLoading(true)
            setIsExtracting(true)
            setError("")
            try {
                const existing = await getExtractionStatus(id)

                if (existing?.status === "completed") {
                    applyExtraction(existing)
                    setIsLoading(false)
                    setIsExtracting(false)
                    return
                }

                if (existing?.status === "processing") {
                    applyExtraction(existing)
                    startPollingFn()
                    return
                }

                if (existing?.status === "failed") {
                    setError(existing.error || "Failed to load extraction data")
                    setIsLoading(false)
                    setIsExtracting(false)
                    return
                }

                const data = await getExtractionData(id, articleUniqueIds)
                if (data?.status === "completed") {
                    applyExtraction(data)
                    setIsLoading(false)
                    setIsExtracting(false)
                } else if (data?.status === "processing" || data?.status === "pending") {
                    applyExtraction(data)
                    startPollingFn()
                } else if (Array.isArray(data?.extractionColumns)) {
                    applyExtraction(data)
                    setIsLoading(false)
                    setIsExtracting(false)
                } else {
                    setIsLoading(false)
                    setIsExtracting(false)
                }
            } catch (err) {
                setError(err?.message || "Failed to load extraction data")
                setIsLoading(false)
                setIsExtracting(false)
            }
        }
        fetchExtraction()

        return clearPoll
    }, [id, isExtractionDefinitionStep, articleIdsSignature, startPolling, stopPolling, articleUniqueIds])
    const getFieldValuesForArticleColumn = (articleId, columnId) => {
        if (articleId == null || !columnId) return {}
        const article =
            extractionArticleValues[articleId] ??
            extractionArticleValues[String(articleId)] ??
            extractionArticleValues[Number(articleId)]
        if (article == null || Array.isArray(article)) return {}
        const columnData = article[columnId] ?? article[String(columnId)]
        return columnData != null && typeof columnData === "object" && !Array.isArray(columnData) ? columnData : {}
    }

    const hasResults =
        !error &&
        articleUniqueIds.length > 0 &&
        (extractionColumns.length > 0 || Object.keys(extractionArticleValues).length > 0)

    const handleAddExtractionColumn = async ({ name, instructions, answerStructure }) => {
        if (!id) throw new Error("Literature review job id is missing")
        if (!articleUniqueIds.length) throw new Error("No articles to extract")

        const result = await createExtractionNewColumn(id, {
            columnName: name,
            includedArticleIds: articleUniqueIds,
            answerStructure: answerStructure || "any",
            ...(instructions != null && String(instructions).trim() !== ""
                ? { instructions: String(instructions).trim() }
                : {}),
        })

        if (Array.isArray(result?.extractionColumns)) {
            setColumnsList(result.extractionColumns)
        } else {
            const newCol = result?.column ?? result?.newColumn
            if (newCol?.id) {
                setColumnsList((prev) => [
                    ...prev.filter((c) => c?.id !== newCol.id),
                    {
                        id: newCol.id,
                        label: newCol.label ?? name,
                        screeningQuestion: newCol.screeningQuestion,
                    },
                ])
            }
        }

        if (Array.isArray(result?.extractionColumns)) {
            setExtractionColumns(result.extractionColumns)
        }
        if (
            result?.extractionArticleValues != null &&
            typeof result.extractionArticleValues === "object" &&
            !Array.isArray(result.extractionArticleValues)
        ) {
            setExtractionArticleValues(result.extractionArticleValues)
        }
    }

    const closeColumnPanel = () => {
        setSidebarView("default")
        setEditingColumn(null)
    }

    const handleEditColumn = (col) => {
        setEditingColumn({ id: col.id, name: col.label, instructions: col.instructions ?? "", answerStructure: col.answerStructure ?? "any" })
        setSidebarView("edit-column")
    }

    const handleUpdateExtractionColumn = async ({ columnId, name, instructions, answerStructure }) => {
        if (!id) throw new Error("Literature review job id is missing")
        const result = await reextractColumn(id, {
            columnId,
            columnName: name,
            instructions,
            includedArticleIds: articleUniqueIds,
            answerStructure: answerStructure || "any",
        })
        if (Array.isArray(result?.extractionColumns)) {
            setExtractionColumns(result.extractionColumns)
            setColumnsList(result.extractionColumns)
        }
        if (
            result?.extractionArticleValues != null &&
            typeof result.extractionArticleValues === "object" &&
            !Array.isArray(result.extractionArticleValues)
        ) {
            setExtractionArticleValues(result.extractionArticleValues)
        }
    }

    const handleDeleteExtractionColumn = async (columnId) => {
        if (!id) throw new Error("Literature review job id is missing")
        const result = await deleteExtractionColumn(id, columnId)
        if (Array.isArray(result?.extractionColumns)) {
            setExtractionColumns(result.extractionColumns)
            setColumnsList(result.extractionColumns)
        }
        if (
            result?.extractionArticleValues != null &&
            typeof result.extractionArticleValues === "object" &&
            !Array.isArray(result.extractionArticleValues)
        ) {
            setExtractionArticleValues(result.extractionArticleValues)
        }
    }

    const mainContent = (
        <div className="extraction-definition-content">
            {isLoading && !hasResults && (
                <div className="extraction-definition-loading">
                    <CircularProgress sx={{ color: "#0d9488" }} size={40} />
                    <p className="extraction-definition-loading-message">Extracting data...</p>
                </div>
            )}
            {!isLoading && error && (
                <div className="extraction-definition-error">
                    <p className="source-card-status">{error}</p>
                </div>
            )}
            {hasResults && isExtracting && (
                <div className="extraction-definition-evaluating-banner">
                    <CircularProgress sx={{ color: "#0d9488" }} size={16} />
                    <span>Extracting remaining articles…</span>
                </div>
            )}
            {hasResults && (
                <>
                    <div className="extraction-definition-table-wrapper">
                        <table className="extraction-definition-table">
                            <thead>
                                <tr>
                                    <th className="extraction-definition-paper-col">Paper</th>
                                    {extractionColumns.map((col) => {
                                        const isBuiltin = col.type === "builtin"
                                        if (isBuiltin) {
                                            return (
                                                <th key={col.id} className="extraction-definition-col-header--locked">
                                                    <Lock sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle", color: "#888" }} />
                                                    {col.label ?? col.id ?? ""}
                                                </th>
                                            )
                                        }
                                        return (
                                            <th
                                                key={col.id}
                                                className="extraction-definition-col-header--clickable"
                                                onClick={() => handleEditColumn({ id: col.id, label: col.label, instructions: col.instructions, answerStructure: col.answerStructure })}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleEditColumn({ id: col.id, label: col.label, instructions: col.instructions, answerStructure: col.answerStructure }) }}
                                            >
                                                {col.label ?? col.id ?? ""}
                                            </th>
                                        )
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {articleUniqueIds.map((articleId, rowIndex) => {
                                    const paper = screenedArticles[rowIndex] ?? {}
                                    const title = paper?.title ?? ""
                                    const rawAuthors = paper?.authors
                                    const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ") : (rawAuthors ?? "")
                                    const journal = paper?.journalTitle ?? ""
                                    const publicationDate = paper?.publicationDate ?? ""
                                    const citations = paper?.citationCount ?? 0
                                    const doi = paper?.doi ?? ""
                                    return (
                                        <tr key={String(articleId)}>
                                            <td className="extraction-definition-paper-cell">
                                                <div className="extraction-definition-paper-card">
                                                    <div className="extraction-definition-paper-title">{title}</div>
                                                    {authors && <div className="extraction-definition-paper-authors">{authors}</div>}
                                                    <div className="extraction-definition-paper-meta">
                                                        <span className="paper-meta-item">
                                                            <MenuBook sx={{ fontSize: 14, color: "#666", mr: 0.5, verticalAlign: "middle" }} />
                                                            {journal},{" "}
                                                        </span>
                                                        <span className="paper-meta-item">{publicationDate}, </span>
                                                        <span className="paper-meta-item">{citations} citations, </span>
                                                        {doi && <DoiButton doi={doi} />}
                                                    </div>
                                                </div>
                                            </td>
                                            {extractionColumns.map((col) => {
                                                const fieldValues = getFieldValuesForArticleColumn(articleId, col.id)
                                                const fields = Array.isArray(col.fields) ? col.fields : []
                                                const isBuiltin = col.type === "builtin"
                                                const isYesNoMaybe = col.answerStructure === "yes_no_maybe"
                                                return (
                                                    <td key={col.id} className="extraction-definition-field-cell">
                                                        {isYesNoMaybe ? (
                                                            <span>{fieldValues["answer"] ?? "—"}</span>
                                                        ) : (
                                                        <ul className="extraction-definition-field-list">
                                                            {fields.map((field, idx) => {
                                                                const value = fieldValues[field.name] ?? fieldValues[field.id] ?? "—"
                                                                const valueStr = String(value)
                                                                const displayLabel = field.label ?? field.description ?? field.name ?? ""
                                                                const fieldKey = `${articleId}|${col.id}|${field.name ?? field.id ?? idx}`
                                                                const isLong = valueStr.length > FIELD_VALUE_CLAMP_THRESHOLD
                                                                const isExpanded = expandedFieldKeys.has(fieldKey)
                                                                const shouldClamp = isLong && !isExpanded
                                                                return (
                                                                    <li key={field.name ?? field.id ?? idx}>
                                                                        <div className={shouldClamp ? "extraction-definition-field-item--clamped" : undefined}>
                                                                            <span className="extraction-definition-field-sep">-</span> {displayLabel}<span className="extraction-definition-field-sep">:</span> {valueStr}
                                                                        </div>
                                                                        {isLong && (
                                                                            <button
                                                                                type="button"
                                                                                className="extraction-definition-field-toggle"
                                                                                onClick={() => toggleFieldExpanded(fieldKey)}
                                                                            >
                                                                                {isExpanded ? "Show less" : "Show more"}
                                                                            </button>
                                                                        )}
                                                                    </li>
                                                                )
                                                            })}
                                                        </ul>
                                                        )}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )


    const sidebarDefault = (
        <div className="extraction-definition-sidebar">
            <div className="extraction-definition-sidebar-section">
                <h3 className="extraction-definition-sidebar-title">
                    Columns{" "}
                    <span
                        className="extraction-definition-sidebar-icon"
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsScreeningSectionHidden(false)
                        }}
                    >
                        ℹ️
                    </span>
                </h3>
            </div>

            {!isScreeningSectionHidden && (
                <div className="extraction-definition-sidebar-section">
                    <div className="extraction-definition-sidebar-header">
                        <h4>Extraction columns</h4>
                    </div>
                    <div className="extraction-definition-sidebar-content">
                        <p>
                            Use suggested extraction columns or add your own. Evaluate extraction results and refine column definitions as needed.                        </p>
                        <a
                            href="#"
                            className="extraction-definition-sidebar-link"
                            onClick={(e) => {
                                e.preventDefault()
                                setIsScreeningSectionHidden(true)
                            }}
                        >
                            Hide
                        </a>
                    </div>
                </div>
            )}

            <div className="extraction-definition-sidebar-columns-block">
                <h3 className="extraction-definition-sidebar-column">Extraction columns</h3>
                <button
                    type="button"
                    className="extraction-definition-add-new-link"
                    onClick={() => setSidebarView("add-column")}
                >
                    <Add sx={{ fontSize: 18 }} />
                    Add new column
                </button>
            </div>
            <ul className="extraction-definition-sidebar-column-list">
                {columnDefs.map((col) => {
                    const fullCol = extractionColumns.find((c) => c.id === col.id) ?? col
                    const isBuiltin = fullCol.type === "builtin"
                    if (isBuiltin) {
                        return (
                            <li
                                key={col.id}
                                className="extraction-definition-sidebar-column-item extraction-definition-sidebar-column-item--locked"
                                title="Built-in column (locked)"
                            >
                                <Lock className="extraction-definition-sidebar-drag-icon" fontSize="small" />
                                <span>{col.label}</span>
                            </li>
                        )
                    }
                    return (
                        <li
                            key={col.id}
                            className="extraction-definition-sidebar-column-item extraction-definition-sidebar-column-item--clickable"
                            onClick={() => handleEditColumn({ id: fullCol.id, label: fullCol.label ?? col.label, instructions: fullCol.instructions, answerStructure: fullCol.answerStructure })}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleEditColumn({ id: fullCol.id, label: fullCol.label ?? col.label, instructions: fullCol.instructions, answerStructure: fullCol.answerStructure }) }}
                        >
                            <DragIndicator className="extraction-definition-sidebar-drag-icon" fontSize="small" />
                            <span>{col.label}</span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )

    const sidebar =
        sidebarView === "add-column" ? (
            <div className="extraction-definition-sidebar extraction-definition-sidebar--add-column">
                <AddNewColumnPanel
                    variant="extraction"
                    onClose={closeColumnPanel}
                    onAdd={handleAddExtractionColumn}
                />
            </div>
        ) : sidebarView === "edit-column" ? (
            <div className="extraction-definition-sidebar extraction-definition-sidebar--add-column">
                <AddNewColumnPanel
                    variant="extraction"
                    onClose={closeColumnPanel}
                    onUpdate={handleUpdateExtractionColumn}
                    onDelete={handleDeleteExtractionColumn}
                    editingColumn={editingColumn}
                />
            </div>
        ) : (
            sidebarDefault
        )

    const handleNext = () => {
        setWorkflowData({
            extractionArticleValues,
            extractionColumns,
        })
        navigateToStep("data-extraction-results")
    }

    const handleBack = () => {
        const backStep = fromStep ?? "recommended-abstracts"
        if (backStep === "recommended-sources") {
            setFromStep("recommended-sources")
            navigateToStep("recommended-sources")
        } else {
            setFromStep("recommended-abstracts")
            navigateToStep("recommended-abstracts")
        }
    }

    return (
        <LiteratureReviewLayout
            projectTitle={workflowData.litreviewtitle || question || "Data extraction definition"}
            editableTitle={!!id}
            steps={steps}
            mainContent={mainContent}
            sidebar={sidebar}
            onNext={handleNext}
            onBack={handleBack}
            nextLabel="View extraction results"
            showBack={true}
            showNext={hasResults && !isExtracting}
        />
    )
}

export default ExtractionDefiniton